import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { currencySymbols } from "../../../constants/currency.constants";
import { v4 as uuidv4 } from "uuid";
import "dayjs/locale/en-gb";

import {
  paymentFrequencyOptions,
  paymentTermFrequencyOptions,
  sessionsFrequencyOptions,
  paymentMethodOptions,
  paymentTypeOptions,
} from "../../../constants/clientProfile.constants";
import theme from "../../../theme";
import { ErrorMessage } from "../../../utils/commonUtils";
import { SUCCESS } from "../../../utils/constants";
import { SubscriptionsActions } from "../../api/RequestUtil";
import AlertService from "../../common/Alert/alertService";

export const Label = styled("label")({
  minWidth: "120px",
  display: "flex",
  paddingTop: "10px",
  color: theme.palette.common.black,
  fontSize: "16px",
  fontWeight: 500,
});

const SubscriptionFormFields = ({
  register,
  watch,
  setValue,
  getValues,
  trigger,
  errors,
  startDateLabel = "Start Date",
  hasEwayCredentials = false,
  credentialsChecked = true,
  isFreeSubscription = false,
}) => {
  const { error } = AlertService();
  const { dateFormat } = useSelector((state) => state.userSettings);
  // eslint-disable-next-line no-unused-vars
  const [loader, setLoader] = useState(false);
  const [paymentCategoryList, setPaymentCategoryList] = useState([]);
  const { currency } = useSelector((state) => state.userSettings);
  const currentCurrencySymbol = currencySymbols.find((curr) => curr.value === currency)?.name;
  const [addCategoryLoader, setAddCategoryLoader] = useState(false);

  const fetchPaymentCategoryList = () => {
    setLoader(true);
    SubscriptionsActions.getPaymentCategoryList({})
      .then(({ data }) => {
        setPaymentCategoryList(data.data);
      })
      .catch(({ data }) => error(ErrorMessage(data)))
      .finally(() => setLoader(false));
  };

  useEffect(() => {
    fetchPaymentCategoryList();
  }, []);

  const paymentTermFrequency = getValues("paymentTermFrequency");
  useEffect(() => {
    if (paymentTermFrequency === "ongoing") {
      setValue("paymentTermNumber", 1, { shouldValidate: false });
    }
  }, [paymentTermFrequency, setValue]);

  // When payment method is automatic, force payment type to eway
  // Also reset to manual if automatic was selected but no eWay credentials
  const paymentMethods = watch("paymentMethods");
  useEffect(() => {
    if (!credentialsChecked) return;
    if (paymentMethods === "automatic") {
      if (hasEwayCredentials) {
        setValue("paymentTypes", "eway", { shouldValidate: true });
      } else {
        // No eWay credentials, can't use automatic - reset to manual
        setValue("paymentMethods", "manual", { shouldValidate: true });
        setValue("paymentTypes", "cash", { shouldValidate: true });
      }
    }
    // Also ensure payment type isn't eway if no credentials
    const currentPaymentType = getValues("paymentTypes");
    if (!hasEwayCredentials && currentPaymentType === "eway") {
      setValue("paymentTypes", "cash", { shouldValidate: true });
    }
  }, [paymentMethods, setValue, hasEwayCredentials, getValues, credentialsChecked]);

  const handleAddNewCategory = (categoryName) => {
    const newPaymentCategoryCategory = {
      id: uuidv4(),
      name: categoryName,
    };

    setAddCategoryLoader(true);

    SubscriptionsActions.addPaymentCategory(newPaymentCategoryCategory)
      .then(({ data }) => {
        if (data.status === SUCCESS) {
          setPaymentCategoryList((prev) => [...prev, newPaymentCategoryCategory]);
          setValue("paymentCategory", newPaymentCategoryCategory.id, { shouldValidate: true, shouldDirty: true });
          trigger("paymentCategory");
        }
      })
      .catch(({ data }) => error(ErrorMessage(data)))
      .finally(() => setAddCategoryLoader(false));
  };

  const handleOnChange = (e, selectedOption) => {
    if (selectedOption) {
      setValue("paymentCategory", selectedOption.id, { shouldValidate: true, shouldDirty: true });
      trigger("paymentCategory");
    }
  };

  // Compute the selected payment category value
  const currentPaymentCategoryId = watch("paymentCategory");

  // Find the matching payment category object from the list
  // If the ID doesn't exist in the list, return null (which prevents MUI warnings)
  const autocompleteValue = useMemo(() => {
    if (!currentPaymentCategoryId) return null;
    const found = paymentCategoryList?.find((option) => option.id === currentPaymentCategoryId);
    return found || null;
  }, [currentPaymentCategoryId, paymentCategoryList]);

  // Check if we're waiting for the payment category list to load
  // Show loading if: list is empty AND we have a payment category ID to look for
  const isLoadingPaymentCategory = currentPaymentCategoryId && paymentCategoryList.length === 0;

  return (
    <>
      <Box>
        <InputLabel shrink htmlFor="startDate" sx={{ fontWeight: 500, mb: 0.25 }}>
          {startDateLabel}
        </InputLabel>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
          <DatePicker
            format={dateFormat.toUpperCase()}
            views={["year", "month", "day"]}
            value={watch("startDate") ? dayjs(watch("startDate")) : null}
            slotProps={{
              textField: {
                id: "startDate",
                fullWidth: true,
                inputProps: {
                  style: { cursor: "pointer" },
                },
                sx: {
                  maxWidth: "400px",
                },
              },
            }}
            sx={{
              maxWidth: "400px",
              width: "100%",
              "& .MuiOutlinedInput-root": {
                background: theme.palette.white.light,
                borderRadius: "12px",
                cursor: "pointer",
                "&:hover": {
                  background: theme.palette.white.main,
                },
              },
              "& .MuiOutlinedInput-input": {
                cursor: "pointer",
              },
            }}
            onChange={(newValue) => {
              setValue("startDate", newValue ? newValue.toDate() : null, { shouldDirty: true });
              trigger("startDate");
            }}
          />
          <Typography variant="body2" color="error" sx={{ fontSize: 12, margin: "3px 14px 0px" }}>
            {errors.startDate?.message}
          </Typography>
        </LocalizationProvider>
      </Box>

      {!isFreeSubscription && (
        <Box>
          <InputLabel shrink htmlFor="nextPaymentDate" sx={{ fontWeight: 500, mb: 0.25 }}>
            Next Payment Date
          </InputLabel>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
            <DatePicker
              format={dateFormat.toUpperCase()}
              views={["year", "month", "day"]}
              value={watch("nextPaymentDate") ? dayjs(watch("nextPaymentDate")) : null}
              slotProps={{
                textField: {
                  id: "nextPaymentDate",
                  fullWidth: true,
                  inputProps: {
                    style: { cursor: "pointer" },
                  },
                  sx: {
                    maxWidth: "400px",
                  },
                },
              }}
              sx={{
                maxWidth: "400px",
                width: "100%",
                "& .MuiOutlinedInput-root": {
                  background: theme.palette.white.light,
                  borderRadius: "12px",
                  cursor: "pointer",
                  "&:hover": {
                    background: theme.palette.white.main,
                  },
                },
                "& .MuiOutlinedInput-input": {
                  cursor: "pointer",
                },
              }}
              minDate={dayjs()}
              onChange={(newValue) => {
                setValue("nextPaymentDate", newValue ? newValue.toDate() : null, { shouldDirty: true });
                trigger("nextPaymentDate");
              }}
            />
            <Typography variant="body2" color="error" sx={{ fontSize: 12, margin: "3px 14px 0px" }}>
              {errors.nextPaymentDate?.message}
            </Typography>
          </LocalizationProvider>
        </Box>
      )}

      <Stack direction="row" spacing={2} justifyContent="flex-start" alignItems="flex-start">
        <Label>Payment Term</Label>
        <TextField
          fullWidth
          type="number"
          disabled={getValues("paymentTermFrequency") === "ongoing"}
          {...register("paymentTermNumber", { valueAsNumber: true })}
          error={!!errors.paymentTermNumber}
          helperText={errors.paymentTermNumber?.message}
          inputProps={{ min: 1 }}
          sx={{ minWidth: "60px", width: "auto", margin: "0px !important" }}
        />

        <FormControl fullWidth sx={{ marginLeft: "8px !important" }}>
          <Select
            value={watch("paymentTermFrequency")}
            {...register("paymentTermFrequency")}
            error={!!errors.paymentTermFrequency}
            IconComponent={KeyboardArrowDownIcon}
          >
            <MenuItem value="" disabled>
              <Typography
                color={theme.palette.gray.light}
                height="100%"
                display="flex"
                alignItems="center"
                fontSize={16}
              >
                Payment Term Freq.
              </Typography>
            </MenuItem>
            {paymentTermFrequencyOptions.map((type) => (
              <MenuItem value={type.value} key={type.value}>
                {type.name}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="body2" color="error" sx={{ fontSize: 12, margin: "3px 14px 0px" }}>
            {errors.paymentTermFrequency?.message}
          </Typography>
        </FormControl>
      </Stack>

      <Box>
        <InputLabel shrink htmlFor="paymentAmount" sx={{ fontWeight: 500, mb: 0.25 }}>
          Payment Amount
        </InputLabel>
        <Stack
          direction="row"
          alignItems="center"
          sx={{ background: theme.palette.white.light, borderRadius: "12px", paddingLeft: 1 }}
        >
          <Typography fontSize="16px" fontWeight="500">
            {currentCurrencySymbol}
          </Typography>
          <TextField
            id="paymentAmount"
            type="number"
            {...register("paymentAmount", { valueAsNumber: true })}
            fullWidth
            error={!!errors.paymentAmount}
            helperText={errors.paymentAmount?.message}
            inputProps={{ min: 1 }}
          />
        </Stack>
      </Box>

      <Box>
        <InputLabel shrink htmlFor="paymentFrequency" sx={{ fontWeight: 500, mb: 0.25 }}>
          Payment Frequency
        </InputLabel>
        <FormControl fullWidth>
          <Select
            id="paymentFrequency"
            value={watch("paymentFrequency")}
            {...register("paymentFrequency")}
            error={!!errors.paymentFrequency}
            IconComponent={KeyboardArrowDownIcon}
            displayEmpty
          >
            <MenuItem value="" disabled>
              <Typography
                color={theme.palette.gray.light}
                height="100%"
                display="flex"
                alignItems="center"
                fontSize={16}
              >
                Select frequency
              </Typography>
            </MenuItem>
            {paymentFrequencyOptions.map((type) => (
              <MenuItem value={type.value} key={type.value}>
                {type.name}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="body2" color="error" sx={{ fontSize: 12, margin: "3px 14px 0px" }}>
            {errors.paymentFrequency?.message}
          </Typography>
        </FormControl>
      </Box>

      <Box>
        <InputLabel shrink htmlFor="paymentCategory" sx={{ fontWeight: 500, mb: 0.25 }}>
          Payment Category
        </InputLabel>
        {addCategoryLoader || isLoadingPaymentCategory ? (
          <Box
            sx={{
              background: theme.palette.white.light,
              borderRadius: "12px",
              padding: "12px 8px",
            }}
          >
            <CircularProgress size={16} sx={{ display: "flex", justifyContent: "center", alignItems: "center" }} />
          </Box>
        ) : (
          <Autocomplete
            key={`payment-category-${currentPaymentCategoryId || "none"}`}
            freeSolo
            disableClearable
            options={paymentCategoryList}
            getOptionLabel={(option) => {
              // Handle both object and string values gracefully
              if (typeof option === "string") return "";
              return option?.name ?? "";
            }}
            value={autocompleteValue}
            onChange={handleOnChange}
            isOptionEqualToValue={(option, value) => {
              // Be very forgiving - don't throw warnings for mismatches
              if (!option || !value) return false;
              if (typeof value === "string") return false;
              return option.id === value.id;
            }}
            filterOptions={(options, state) => {
              // Don't filter if the input value looks like a UUID (likely an invalid initial value)
              if (
                state.inputValue &&
                state.inputValue.match(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i)
              ) {
                return options;
              }
              // Default filter behavior
              return options.filter((option) => option.name.toLowerCase().includes(state.inputValue.toLowerCase()));
            }}
            renderOption={(props, item) => (
              <li {...props} key={item.id}>
                <>{item.name}</>
              </li>
            )}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                const currOptionValue = e.target.value;
                const isOptionFromList = paymentCategoryList.find(
                  (option) => option.name.toLowerCase() === currOptionValue?.toLowerCase()
                );

                if (isOptionFromList === undefined) {
                  handleAddNewCategory(currOptionValue);
                }
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                id="paymentCategory"
                error={!!errors.paymentCategory}
                helperText={
                  errors.paymentCategory ? errors.paymentCategory.message : "Press 'Enter' to add a new category"
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    padding: 0,
                  },
                  "& .MuiAutocomplete-input": {
                    padding: "0px 8px !important",
                  },
                }}
              />
            )}
          />
        )}
      </Box>

      <Stack direction="row" spacing={2}>
        <TextField
          fullWidth
          type="number"
          {...register("sessionsNumber", { valueAsNumber: true })}
          error={!!errors.sessionsNumber}
          helperText={errors.sessionsNumber?.message}
          inputProps={{ min: 1 }}
          sx={{ minWidth: "70px", width: "auto", margin: "0px !important" }}
        />
        <Label>session/s per</Label>
        <FormControl fullWidth sx={{ marginLeft: "8px !important" }}>
          <Select
            value={watch("sessionsFrequency")}
            {...register("sessionsFrequency")}
            error={!!errors.sessionsFrequency}
            IconComponent={KeyboardArrowDownIcon}
          >
            <MenuItem value="" disabled>
              <Typography
                color={theme.palette.gray.light}
                height="100%"
                display="flex"
                alignItems="center"
                fontSize={16}
              >
                Sessions Frequency
              </Typography>
            </MenuItem>
            {sessionsFrequencyOptions.map((type) => (
              <MenuItem value={type.value} key={type.value}>
                {type.name}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="body2" color="error" sx={{ fontSize: 12, margin: "3px 14px 0px" }}>
            {errors.sessionsFrequency?.message}
          </Typography>
        </FormControl>
      </Stack>
      {!isFreeSubscription && (
        <>
          <Box>
            <InputLabel shrink htmlFor="paymentMethods" sx={{ fontWeight: 500, mb: 0.25 }}>
              Payment Method
            </InputLabel>
            <FormControl fullWidth>
              <Select
                id="paymentMethods"
                value={watch("paymentMethods")}
                {...register("paymentMethods")}
                IconComponent={KeyboardArrowDownIcon}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  <Typography
                    color={theme.palette.gray.light}
                    height="100%"
                    display="flex"
                    alignItems="center"
                    fontSize={16}
                  >
                    Select payment method
                  </Typography>
                </MenuItem>
                {paymentMethodOptions
                  .filter((type) => hasEwayCredentials || type.value !== "automatic")
                  .map((type) => (
                    <MenuItem value={type.value} key={type.value}>
                      {type.name.charAt(0).toUpperCase() + type.name.slice(1)}
                    </MenuItem>
                  ))}
              </Select>
              <Typography variant="body2" color="error" sx={{ fontSize: 12, margin: "3px 14px 0px" }}>
                {errors.paymentMethods?.message}
              </Typography>
            </FormControl>
          </Box>
          <Box>
            <InputLabel shrink htmlFor="paymentTypes" sx={{ fontWeight: 500, mb: 0.25 }}>
              Payment Type
            </InputLabel>
            <FormControl fullWidth>
              <Select
                id="paymentTypes"
                value={watch("paymentTypes")}
                {...register("paymentTypes")}
                IconComponent={KeyboardArrowDownIcon}
                displayEmpty
                disabled={watch("paymentMethods") === "automatic"}
              >
                <MenuItem value="" disabled>
                  <Typography
                    color={theme.palette.gray.light}
                    height="100%"
                    display="flex"
                    alignItems="center"
                    fontSize={16}
                  >
                    Select types
                  </Typography>
                </MenuItem>
                {watch("paymentMethods") === "automatic" ? (
                  <MenuItem value="eway">eWay</MenuItem>
                ) : (
                  paymentTypeOptions
                    .filter((type) => hasEwayCredentials || type.value !== "eway")
                    .map((type) => (
                      <MenuItem value={type.value} key={type.value}>
                        {type.name}
                      </MenuItem>
                    ))
                )}
              </Select>
              <Typography variant="body2" color="error" sx={{ fontSize: 12, margin: "3px 14px 0px" }}>
                {errors.paymentTypes?.message}
              </Typography>
            </FormControl>
          </Box>
        </>
      )}
      <Stack direction="row" alignItems="center" gap="6px" sx={{ cursor: "pointer" }}>
        <Checkbox
          {...register("generateInvoices")}
          checked={watch("generateInvoices") ?? false}
          onChange={(e) => {
            setValue("generateInvoices", e.target.checked, {
              shouldValidate: true,
              shouldDirty: true,
              shouldTouch: true,
            });
          }}
          icon={
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                border: "1px solid gray",
                display: "inline-block",
              }}
            />
          }
          checkedIcon={<CheckCircleIcon sx={{ width: 20, height: 20 }} />}
          name="generateInvoices"
        />
        <Typography variant="body2" fontWeight="700">
          Generate Invoices
        </Typography>
      </Stack>
    </>
  );
};

SubscriptionFormFields.propTypes = {
  register: PropTypes.func.isRequired,
  watch: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  getValues: PropTypes.func.isRequired,
  trigger: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  startDateLabel: PropTypes.string,
  selectedFile: PropTypes.string,
  handleFileChange: PropTypes.func,
  handleRemoveFile: PropTypes.func,
  subscriptionList: PropTypes.array,
  hasEwayCredentials: PropTypes.bool,
  credentialsChecked: PropTypes.bool,
  isFreeSubscription: PropTypes.bool,
};

export default SubscriptionFormFields;
